// WelcomeModal.tsx
//
// VERSIONING SYSTEM:
// - This modal uses a version number system to show updates to existing users
// - When you make significant changes to the modal content, increment WELCOME_MODAL_VERSION
// - Also update CURRENT_WELCOME_MODAL_VERSION in App.tsx to match
// - Users who have seen an older version will see the updated modal once
//
// Current version: 3 (Multi-page modal with 9 sections)

import React, { useState } from 'react';
import Modal from 'ui/Modal'; // Adjust the import path as necessary
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from 'context/ThemeContext';
import { isBetaVersion } from 'util/appVersion';
import ReleaseNotesModal from 'components/Common/ReleaseNotesModal';
import MediaCarousel from 'ui/MediaCarousel';
import type { ExistingMediaFile } from 'util/mediaTypes';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext, CarouselDots } from 'ui/Carousel';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface PageContent {
  title: string;
  content: React.ReactNode;
  imagePlaceholder?: string;
  demoVideos?: ExistingMediaFile[];
}

const WelcomeModal: React.FC<Props> = ({ open, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const { theme } = useTheme();
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showAllPages, setShowAllPages] = useState(false);
  
  // Determine if this is a beta version
  const isBeta = isBetaVersion();

  // Helper function to create demo video media files
  const createDemoVideo = (filename: string, order: number = 0): ExistingMediaFile => ({
    key: `demos/${filename}`,
    type: "video",
    originalName: filename,
    order,
    mimetype: "video/quicktime",
    extended_metadata: {
      aspectRatio: 9/16, // Assuming standard video aspect ratio
    }
  });

  const pages: PageContent[] = [
    {
      title: "", // Logo will be displayed in the modal header
      content: (
        <div className="space-y-0.5 text-sm">
          <div>
            <p className="my-0 text-left">
            <span className="soc-standout-text">The world seems like its on a downward spiral.</span> We see and share content on social media about so many different issues, yet the pace of positive change seems glacier—and negative change relentless.
            </p>

            <p className="text-left soc-standout-text">
              But what can we do?
            </p>

            <p className="text-left">We need <span className="soc-standout-text">continuous analysis, <a href="https://en.wikipedia.org/wiki/SWOT_analysis" target="_blank" rel="noopener noreferrer" className="underline soc-standout-text">SWOT</a>-style assessment</span> and <span className="soc-standout-text"><a href="https://en.wikipedia.org/wiki/Pareto_principle" target="_blank" rel="noopener noreferrer" className="underline soc-standout-text">Pareto</a> prioritization of issues</span> and how they influence each other, to <span className="soc-standout-text">develop effective solutions</span>; and we need to better understand each other to <span className="soc-standout-text">unite for action</span>. 
            </p>
            
            <p className="text-left">
              <span className="soc-standout-text">Here's how you can drive positive change with Societea.</span>
            </p> 
            <img 
              src={theme === 'light' ? '/soc_vs_light_v2.svg' : '/soc_vs_dark_v2.svg'} 
              alt="Societea Value Stream" 
              className="my-2 mx-auto w-full h-auto"
            />
            <ul className="list-disc pl-5 space-y-1 text-left">
              <li><span className="soc-standout-text">Knowledge:</span> assess, contextualize, and debate issues</li>
              <li><span className="soc-standout-text">Connection:</span> share your views for more effective discourse and collaboration</li>
              <li><span className="soc-standout-text">Action:</span> coordinate and participate in real-world change</li>
              <li><span className="soc-standout-text">Governance:</span> propose and prioritize improvements to the experience and how <span className="soc-standout-text">we</span> work together</li>
            </ul>

            <p className="text-left">
              We believe <span className="soc-standout-text">everyone is an activist</span>—let's get to work!
            </p>

            
            <p className="text-left italic soc-standout-text font-normal">
              Spill the tea, read the leaves, brew social change.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Brewed differently",
      content: (
        <div className="space-y-3 text-sm">
          <p className="text-left my-0">
            Existing apps weren't built for social change—they were built to enrich a few at the expense of your time and mental health. Societea is different. Here are a few key shifts:
          </p>

          {/* Carousel for the three shifts */}
          <div className="welcome-shifts-carousel-container">
            <Carousel
              key="brewed-differently-carousel"
              opts={{
                align: "start",
              }}
              className="w-full"
            >
              <CarouselContent>
                {/* stakeholder → shareholder section */}
                <CarouselItem>
                  <div className="space-y-2">
                    <div className="text-left text-base font-medium">
                      <p className="soc-standout-text my-0">Stakeholder → Shareholder</p>
                      <p className="text-left my-0 text-sm">
                        You're more than a stakeholder—our long-term vision is to ensure that every user is a shareholder; we just need to work through legal and operational hurdles.
                        </p>
                    </div>
                    <ul className="list-disc pl-5 space-y-2 text-left">
                      <li><span className="soc-standout-text">Shape the company:</span> Feedback isn't limited to the app—company feedback is first-class, too.</li>
                      <li><span className="soc-standout-text">User-driven:</span> Each user is eligible to receive 1 Class U <span className="soc-standout-text">share with voting rights</span> in <a href="https://www.societea.io/" target="_blank" rel="noopener noreferrer" className="external-link">Societea, Inc.</a> (<span className="italic">see policy</span>).</li>
                      <li><span className="soc-standout-text">Beyond points/karma:</span> Users can earn User Dividend Units through participation, tied to a <span className="soc-standout-text">majority share of profits.</span> (<span className="italic">Planned, pending legal validation.</span>)</li>
                    </ul>
                  </div>
                </CarouselItem>

                {/* profit → purpose section */}
                <CarouselItem>
                  <div className="space-y-2">
                    <div className="text-left text-base font-medium">
                      <p className="soc-standout-text my-0">Profit → Purpose</p>
                      <p className="text-left my-0 text-sm">
                        We're not in it for the money, but corporations are easier to operate. We want to exploit those mechanisms as a sustainable <a href="https://en.wikipedia.org/wiki/Social_enterprise" target="_blank" rel="noopener noreferrer" className="underline">social enterprise</a>.
                      </p>
                    </div>
                    <ul className="list-disc pl-5 space-y-2 text-left">
                      <li><span className="soc-standout-text">Legally bound mission:</span> We're structured as a <a href="https://en.wikipedia.org/wiki/Benefit_corporation" target="_blank" rel="noopener noreferrer" className="underline">public benefit corporation</a>; promoting civic engagement and digital activism aren't slogans—they're in our charter.</li>
                      <li><span className="soc-standout-text">No forced ads or data sales:</span> Sustainably funded via <span className="soc-standout-text">user-initiated "ad breaks"</span> that you control—we refuse to interrupt your flow.</li>
                      <li><span className="soc-standout-text">Privacy & anonymity:</span> Speak without fear of retaliation or harassment (<span className="italic">within our <a href="https://www.societea.io/policies/" target="_blank" rel="noopener noreferrer" className="external-link">policies</a></span>). <span className="soc-standout-text">We want your participation, not your data.</span></li>
                    </ul>
                  </div>
                </CarouselItem>

                {/* social → societal section */}
                <CarouselItem>
                  <div className="space-y-2">
                    <div className="text-left text-base font-medium">
                      <p className="soc-standout-text my-0">Social → Societal</p>
                      <p className="text-left my-0 text-sm">
                        We recognize that many issues are systemic, and that we need to address them at the societal level.
                      </p>
                    </div>
                    <ul className="list-disc pl-5 space-y-2 text-left">
                      <li><span className="soc-standout-text">Knowledge-focused:</span> Memes are welcome, but here we <span className="soc-standout-text">codify knowledge</span> so it compounds. If you're looking for a meme trove, we recommend <a href="https://www.reddit.com/" target="_blank" rel="noopener noreferrer" className="external-link">Reddit</a>.</li>
                      <li><span className="soc-standout-text">Structure over scroll:</span> We build on <a href="https://en.wikipedia.org/wiki/Wikipedia" target="_blank" rel="noopener noreferrer" className="underline">Wikipedia</a>/<a href="https://en.wikipedia.org/wiki/Wikidata" target="_blank" rel="noopener noreferrer" className="underline">Wikidata</a> so understanding is <span className="soc-standout-text">sourceable and durable</span>.</li>
                      <li><span className="soc-standout-text">Feeds built on graphs:</span> Connect issues to <span className="soc-standout-text">uncover root causes</span>—don't just refresh the feed, read the leaves.</li> 
                    </ul>
                  </div>
                </CarouselItem>
              </CarouselContent>
              
              <div className="flex items-center justify-center gap-2 mt-2">
                <CarouselPrevious className="static" hideWhenDisabled="invisible" />
                <CarouselDots />
                <CarouselNext className="static" hideWhenDisabled="invisible" />
              </div>
            </Carousel>
          </div>

          {/* Footnote */}
          <p className="text-left text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta my-0 pt-1">
            There may be companies or apps that do a few of these things, but we don't know of any (crazy enough) that do all. And, again, you have a voice in this.
          </p>
        </div>
      )
    },
    {
      title: "Steeped in structure",
      content: (
        <div className="space-y-3 text-sm">
          <p className="text-left my-0">
            Societea is built on a refactored version of the <a href="https://en.wikipedia.org/wiki/Business_Motivation_Model" target="_blank" rel="noopener noreferrer" className="underline">Business Motivation Model (BMM)</a>, enabling us to employ this framework for analysis and strategy development toward improving society.
          </p>

          {/* Carousel for the model diagram and four categories */}
          <div className="welcome-structure-carousel-container">
            <Carousel
              key="steeped-in-structure-carousel"
              opts={{
                align: "start",
              }}
              className="w-full"
            >
              <CarouselContent>
                {/* model diagram section */}
                <CarouselItem>
                  <div className="flex justify-center items-center">
                    <img 
                      src={theme === 'light' ? '/soc_model_light.svg' : '/soc_model_dark.svg'} 
                      alt="Societea Model Structure" 
                      className="w-full h-auto max-w-full"
                    />
                  </div>
                </CarouselItem>

                {/* knowledge section */}
                <CarouselItem>
                  <div className="space-y-2">
                    <div className="text-left text-sm font-medium">
                      <p className="soc-standout-text my-0">Knowledge</p>
                    </div>
                    <ul className="list-disc pl-5 space-y-2 text-left text-sm">
                      <li><span className="soc-standout-text">Issues:</span> Societea is organized around issues: anything (sourced from Wikipedia) that has an influence on society. Issues by themselves are not "good" or "bad", but you can set whether you generally have a positive/negative sentiment toward them. (BMM "influencer" object.)</li>
                      <li><span className="soc-standout-text">Impacts:</span> Document the effects that an issue has on society and, optionally, specify whether it's net positive/neutral/negative. (BMM "assessment" object.)</li>
                      <li><span className="soc-standout-text">Solutions:</span> Propose conceptual, strategic approaches to address issues—either minimizing negative or maximizing positive impacts. (BMM "strategy" object.)</li>
                      <li><span className="soc-standout-text">Actions:</span> Where rubber meets the road, actions are practical, measurable ways to tackle an issue; they're ideally inspired by and implement solutions. (BMM "tactic" object.)</li>
                    </ul>
                  </div>
                </CarouselItem>

                {/* discourse section */}
                <CarouselItem>
                  <div className="space-y-2">
                    <div className="text-left text-sm font-medium">
                      <p className="soc-standout-text my-0">Discourse</p>
                    </div>
                    <ul className="list-disc pl-5 space-y-2 text-left text-sm">
                      <li><span className="soc-standout-text">Teabates:</span> We emphasize consensus, and Teabates are structured debates around an issue to help find common ground. Teabates have a single claim which users can agree/disagree with and add arguments for/against.</li>
                      <li><span className="soc-standout-text">Comments:</span> Fully threaded discussions around anything on Societea.</li>
                    </ul>
                  </div>
                </CarouselItem>

                {/* governance section */}
                <CarouselItem>
                  <div className="space-y-2">
                    <div className="text-left text-sm font-medium">
                      <p className="soc-standout-text my-0">Governance</p>
                    </div>
                    <ul className="list-disc pl-5 space-y-2 text-left text-sm">
                      <li><span className="soc-standout-text">Voting:</span> Votes are used to prioritize and drive visibility—not agreement—around something.</li>
                      <li><span className="soc-standout-text">Report:</span> Let the admins know about content that goes against the Content Policy. (We hope this will be increasingly moderated by the community in the future.)</li>
                      <li><span className="soc-standout-text">Feedback:</span> Propose and prioritize changes to how all this (and the company) works.</li>
                    </ul>
                  </div>
                </CarouselItem>

                {/* privacy section */}
                <CarouselItem>
                  <div className="space-y-2">
                    <div className="text-left text-sm font-medium">
                      <p className="soc-standout-text my-0">Privacy</p>
                    </div>
                    <ul className="list-disc pl-5 space-y-2 text-left text-sm">
                      <li><span className="soc-standout-text">Private accounts:</span> Users should be able to share different things with different groups of people—not be siloed into one or the other—so we don't have a binary (public/followers) privacy approach; instead, users can set different privacy levels per item. Private accounts simply come with a default privacy of "followers", which can be changed in your account settings.</li>
                      <li><span className="soc-standout-text">Anonymity:</span> Some views and attempts at organizing can be targets for retaliation or harassment; in order to let you share your knowledge while still protecting you, we offer nearly untraceable posting. We are working on 100% anonymous posting, but need your help to balance your privacy with the safety of the community.</li>
                    </ul>
                  </div>
                </CarouselItem>
              </CarouselContent>
              
              <div className="flex items-center justify-center gap-2 mt-2">
                <CarouselPrevious className="static" hideWhenDisabled="invisible" />
                <CarouselDots />
                <CarouselNext className="static" hideWhenDisabled="invisible" />
              </div>
            </Carousel>
          </div>
        </div>
      )
    },
    {
      title: "Infuse your views",
      content: (
        <p className="text-left text-sm my-0">
          Share your background and views to help everyone better understand your perspective.
        </p>
      ),
      imagePlaceholder: "infuse-views",
      demoVideos: [
        createDemoVideo("demo_user_issuerank.mov", 0),
        createDemoVideo("demo_user_perspective.mov", 1)
      ]
    },
    {
      title: "Spill the tea",
      content: (
        <p className="text-left text-sm my-0">
          Assess the impacts of issues, propose solutions, and more to build our collective knowledge.
        </p>
      ),
      imagePlaceholder: "spill-tea",
      demoVideos: [
        createDemoVideo("demo_add_impact.mov", 0),
        createDemoVideo("demo_crosspost_solution.mov", 1)
      ]
    },
    {
      title: "Read the leaves",
      content: (
        <p className="text-left text-sm my-0">
          Explore and reveal how issues connect by adding influences, and start teabates to glean insights.
        </p>
      ),
      imagePlaceholder: "read-leaves",
      demoVideos: [
        createDemoVideo("demo_add_influence.mov", 0),
        createDemoVideo("demo_start_teabate.mov", 1)
      ]
    },
    {
      title: "Have a sip",
      content: (
        <p className="text-left text-sm my-0">
          Comment and vote to improve understanding and shape priorities.
        </p>
      ),
      imagePlaceholder: "have-sip",
      demoVideos: [
        createDemoVideo("demo_vote_comment.mov", 0)
      ]
    },
    {
      title: "Find your blend",
      content: (
        <p className="text-left text-sm my-0">
          Form parteas to tackle issues, and share and join actions to drive change.
        </p>
      ),
      imagePlaceholder: "find-blend",
      demoVideos: [
        createDemoVideo("demo_create_partea.mov", 0),
        createDemoVideo("demo_join_action.mov", 1)
      ]
    },
    {
      title: "Refine the brew",
      content: (
        <p className="text-left text-sm my-0">
          Contribute feedback to make all this more useful and fun.
        </p>
      ),
      imagePlaceholder: "evolve-brew",
      demoVideos: [
        createDemoVideo("demo_add_feedback.mov", 0)
      ]
    },
  ];

  const handleNext = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleDemos = () => {
    setShowAllPages(true);
    if (currentPage < pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleClose = () => {
    setCurrentPage(0); // Reset to first page when closing
    setShowAllPages(false); // Reset to show only first 3 pages
    onClose();
  };

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === pages.length - 1;
  const currentPageData = pages[currentPage];

  // Custom title component that shows logo for first page, text for others
  const modalTitle = isFirstPage ? (
    <div className="flex justify-center pt-0 pb-1 items-center relative">
      <img
        src={theme === 'light' ? '/soc_app_logo_head_light.svg' : '/soc_app_logo_head_dark.svg'}
        alt="Societea Logo"
        className="h-8 w-auto"
      />
      {isBeta && (
        <>
          <span
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="relative ml-0.5 top-2.5 text-xxs bg-indigo-600 dark:bg-indigo-600 text-white pl-[5px] pr-1 py-0.5 rounded-full hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors flex-shrink-0"
          >
            BETA
          </span>
        </>
      )}
    </div>
  ) : currentPageData.title;

  const footer = (
    <div className="flex flex-col space-y-2 py-2">
      
      {isFirstPage && (
        <div className="space-y-1 mb-2">
          <p className="text-left text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta my-0">
            This is our beta; you may find rough edges and missing features, so please share <a href="/user-feedback" target="_blank" rel="noopener noreferrer" className="external-link">feedback</a>!
          </p>
          <p className="text-left text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta my-0">
            By continuing, you agree to our{' '}
            <a
              href="https://www.societea.io/policies/user-agreement"
              target="_blank"
              rel="noopener noreferrer"
              className="external-link"
            >
              User Agreement
            </a>{' '}
            and acknowledge that you understand the{' '}
            <a
              href="/policies/privacy-policy"
              className="external-link"
            >
              Privacy Policy
            </a>
            .{' '}
            <span className="text-left text-eoe-text-sub dark:text-eoe-dark-text-sub">
              We <i>never</i> sell your data.
            </span>
          </p>
        </div>
      )}
      <div className="flex justify-center space-x-1 mb-2">
        {pages.slice(0, showAllPages ? pages.length : 3).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentPage(index)}
            className={`h-1 rounded-full transition-all cursor-pointer hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-eoe-primary dark:focus:ring-eoe-dark-primary focus:ring-offset-1 ${
              index === currentPage
                ? 'bg-eoe-primary dark:bg-eoe-dark-primary w-12'
                : 'bg-gray-300 dark:bg-gray-600 w-8 hover:bg-gray-400 dark:hover:bg-gray-500'
            }`}
            aria-label={`Go to section ${index + 1}: ${pages[index].title || 'Welcome'}`}
          />
        ))}
      </div>
      
      <div className="flex justify-between items-center w-full">
        <div>
          {!isFirstPage && (
            <button
              onClick={handlePrevious}
              className="btn-secondary flex items-center gap-1 py-2 px-2"
            >
              <ChevronLeft className="size-4" />
              <span className="mr-1">Previous</span>
            </button>
          )}
        </div>
        <div>
          {(currentPage === 2) && (
            <button
              onClick={handleClose}
              className="btn-secondary py-2 px-6"
            >
              Let's go! 🚀
            </button>
          )}
        </div>
        <div>
          {isLastPage ? (
            <button
              onClick={handleClose}
              className="btn-primary py-2 px-6"
            >
              Let's go! 🚀
            </button>
          ) : (
            <button
              onClick={currentPage === 2 ? handleDemos : handleNext}
              className="btn-primary flex items-center gap-1 py-2 px-2"
            >
              <span className="ml-1">{(currentPage === 2) ? 'Demos' : 'Next'}</span>
              <ChevronRight className="size-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Modal isOpen={open} onClose={handleClose} title={modalTitle} footer={footer} stickyFooter bodyDisplayStyle={`${currentPage === 1 || currentPage === 2 ? 'block' : 'flex'}`}>
        <div className="prose dark:prose-invert">
          {currentPageData.content}
          {currentPageData.demoVideos && currentPageData.demoVideos.length > 0 ? (
            <div className="flex justify-center">
              <div className="w-full max-w-lg">
                <MediaCarousel 
                  key={`welcome-carousel-${currentPage}`}
                  mediaFiles={currentPageData.demoVideos}
                  className="rounded-lg w-full overflow-hidden"
                  showDots={currentPageData.demoVideos.length > 1}
                  showArrows={currentPageData.demoVideos.length > 1}
                />
              </div>
            </div>
          ) : currentPageData.imagePlaceholder && (
            <div className="flex justify-center mt-6">
              <div className="w-64 h-48 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <span className="text-gray-500 dark:text-gray-400 text-sm">
                  {currentPageData.imagePlaceholder} image
                </span>
              </div>
            </div>
          )}
        </div>
      </Modal>
      {showReleaseNotes && (
        <ReleaseNotesModal
          isOpen={showReleaseNotes}
          onClose={() => setShowReleaseNotes(false)}
        />
      )}
    </>
  );
};

export default WelcomeModal;
